import {
  DocumentReference,
  isDocRef,
  ISODateTimeWithUTCType,
  TypeGuard,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import type { IPractice } from '../../../practice/practice';
import type { IOvieHealthDateRange } from '../analytics/analytics-common';
import type {
  IOvieHealthCallRecord,
  IOvieHealthCallsListFilters,
  IOvieHealthCallsListResponse,
} from './call-record';

export enum OvieHealthCallsAction {
  ListCalls = 'listCalls',
  GetCall = 'getCall',
}

export function isOvieHealthCallsAction(
  data: unknown
): data is OvieHealthCallsAction {
  return Object.values(OvieHealthCallsAction).includes(
    data as OvieHealthCallsAction
  );
}

// `phone_number` only unmasks the full E.164 number when the API key also
// carries the `pii:phone_number:read` capability; masked `+XX***NNNN` otherwise.
export enum OvieHealthCallInclude {
  PhoneNumber = 'phone_number',
  Summary = 'summary',
}

export function isOvieHealthCallInclude(
  data: unknown
): data is OvieHealthCallInclude {
  return Object.values(OvieHealthCallInclude).includes(
    data as OvieHealthCallInclude
  );
}

export interface IOvieHealthCallsActionPayloadMap {
  listCalls: IOvieHealthCallsListResponse;
  getCall: IOvieHealthCallRecord;
}

export interface IOvieHealthCallsListOptions {
  cursor?: string;
  /**
   * Page size. API default 50, max 200.
   */
  limit?: number;
  filters?: IOvieHealthCallsListFilters;
  include?: OvieHealthCallInclude[];
}

export interface IOvieHealthCallsActionOptionsMap {
  listCalls: IOvieHealthCallsListOptions & { range: IOvieHealthDateRange };
  getCall: { callId: number; include?: OvieHealthCallInclude[] };
}

export interface IOvieHealthCallsRequest<
  A extends OvieHealthCallsAction = OvieHealthCallsAction,
> {
  action: A;
  practiceRef: DocumentReference<IPractice>;
  options: IOvieHealthCallsActionOptionsMap[A];
}

const isCallsListFilters = TypeGuard.interface<IOvieHealthCallsListFilters>({
  callOutcome: TypeGuard.nilOr(isString),
  callerIntent: TypeGuard.nilOr(isString),
  callerSentiment: TypeGuard.nilOr(isString),
  treatmentCategory: TypeGuard.nilOr(isString),
  callSuccessful: TypeGuard.nilOr(isString),
  afterHoursCall: TypeGuard.nilOr(isBoolean),
});

const isCallsListOptions = TypeGuard.interface<
  IOvieHealthCallsActionOptionsMap['listCalls']
>({
  range: TypeGuard.interface<IOvieHealthDateRange>({
    from: isString,
    to: isString,
  }),
  cursor: TypeGuard.nilOr(isString),
  limit: TypeGuard.nilOr(isNumber),
  filters: TypeGuard.nilOr(isCallsListFilters),
  include: TypeGuard.nilOr(TypeGuard.arrayOf(isOvieHealthCallInclude)),
});

const isGetCallOptions = TypeGuard.interface<
  IOvieHealthCallsActionOptionsMap['getCall']
>({
  callId: isNumber,
  include: TypeGuard.nilOr(TypeGuard.arrayOf(isOvieHealthCallInclude)),
});

export function isOvieHealthCallsRequest(
  data: unknown
): data is IOvieHealthCallsRequest {
  return TypeGuard.interface<IOvieHealthCallsRequest>({
    action: isOvieHealthCallsAction,
    practiceRef: isDocRef,
    options: (
      options: unknown
    ): options is IOvieHealthCallsRequest['options'] =>
      isCallsListOptions(options) || isGetCallOptions(options),
  })(data);
}

export interface IOvieHealthCallsResponse<
  A extends OvieHealthCallsAction = OvieHealthCallsAction,
> {
  action: A;
  data: IOvieHealthCallsActionPayloadMap[A];
  fetchedAt: ISODateTimeWithUTCType;
}
